import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axios';
import './DashboardPage.css';
import logo from '../assets/teesergolf-logo.png';
import Navigation from '../components/Navigation';

function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [telegramChatId, setTelegramChatId] = useState('');
  const [isLinkingTelegram, setIsLinkingTelegram] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [linkSuccess, setLinkSuccess] = useState('');

  useEffect(() => {
    fetchAlerts();
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setIsLoadingProfile(true);
    try {
      const response = await axiosInstance.get('/auth/profile');
      setProfile(response.data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleLinkTelegram = async (e) => {
    e.preventDefault();
    setLinkError('');
    setLinkSuccess('');

    if (!telegramChatId.trim()) {
      setLinkError('Please enter your Telegram Chat ID');
      return;
    }

    setIsLinkingTelegram(true);
    try {
      await axiosInstance.post('/auth/link-telegram', {
        telegramChatId: telegramChatId.trim()
      });
      setLinkSuccess('Telegram linked successfully!');
      setTelegramChatId('');
      // Refresh profile to hide the setup guide
      fetchProfile();
    } catch (err) {
      console.error('Error linking telegram:', err);
      setLinkError(err.response?.data?.message || 'Failed to link Telegram. Please try again.');
    } finally {
      setIsLinkingTelegram(false);
    }
  };

  const fetchAlerts = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/alerts');
      setAlerts(response.data);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to load your alerts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (alertId) => {
    try {
      await axiosInstance.patch(`/alerts/${alertId}/toggle`);
      // Refresh alerts to get updated status
      fetchAlerts();
    } catch (err) {
      console.error('Error toggling alert:', err);
      setError('Failed to update alert status. Please try again.');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm('Are you sure you want to delete this alert?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/alerts/${alertId}`);
      // Refresh alerts after deletion
      fetchAlerts();
    } catch (err) {
      console.error('Error deleting alert:', err);
      setError('Failed to delete alert. Please try again.');
    }
  };

  // Format hour to AM/PM
  const formatHour = (hour) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format recurring days
  const formatRecurringDays = (recurringDays) => {
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    if (!recurringDays || recurringDays.length === 0) return '';

    if (recurringDays.length === 7) return 'Every day';
    if (recurringDays.length === 1) return `Every ${daysOfWeek[recurringDays[0]]}`;

    const dayNames = recurringDays.map(dayIndex => daysOfWeek[dayIndex]);
    return `Every ${dayNames.join(', ')}`;
  };

  // Group alerts by course
  const groupedAlerts = alerts.reduce((acc, alert) => {
    if (!acc[alert.courseName]) {
      acc[alert.courseName] = [];
    }
    acc[alert.courseName].push(alert);
    return acc;
  }, {});

  const courseNames = Object.keys(groupedAlerts);

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-logo">
            <img src={logo} alt="TeeserGolf" />
          </div>
          <Navigation />
        </div>
      </header>

      <main className="dashboard-main">
        <div className="dashboard-content">
          <h1>Your Dashboard</h1>
          <p className="dashboard-subtitle">Find and book your perfect tee time</p>

          <div className="quick-action-section">
            <button className="configure-alerts-button" onClick={() => navigate('/tee-time-alerts')}>
              + Configure New Alert
            </button>
          </div>

          {/* Telegram Setup Guide - shown when user hasn't linked Telegram */}
          {!isLoadingProfile && profile && !profile.telegramChatId && (
            <div className="setup-guide">
              <div className="setup-guide-header">
                <h2>How to Receive Alerts</h2>
                <p>Complete these steps to start receiving tee time notifications via Telegram</p>
              </div>
              <div className="setup-steps">
                <div className="setup-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h3>Download Telegram</h3>
                    <p>If you don't have Telegram, download it from the <a href="https://telegram.org/apps" target="_blank" rel="noopener noreferrer">App Store or Google Play</a></p>
                  </div>
                </div>
                <div className="setup-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h3>Start a Chat with TeeserGolfBot</h3>
                    <p>Open Telegram and search for <strong>@TeeserGolfBot</strong>, or <a href="https://t.me/TeeserGolfBot" target="_blank" rel="noopener noreferrer">click here to open the bot directly</a></p>
                  </div>
                </div>
                <div className="setup-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h3>Get Your Chat ID</h3>
                    <p>Send <strong>/start</strong> to the bot. It will reply with your unique Chat ID number</p>
                  </div>
                </div>
                <div className="setup-step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h3>Link Your Account</h3>
                    <p>Enter your Chat ID below to complete setup:</p>
                    <form onSubmit={handleLinkTelegram} className="telegram-link-form">
                      <div className="telegram-input-group">
                        <input
                          type="text"
                          value={telegramChatId}
                          onChange={(e) => setTelegramChatId(e.target.value)}
                          placeholder="Enter your Chat ID"
                          disabled={isLinkingTelegram}
                          aria-label="Telegram Chat ID"
                        />
                        <button
                          type="submit"
                          className="link-telegram-button"
                          disabled={isLinkingTelegram}
                        >
                          {isLinkingTelegram ? 'Linking...' : 'Link Account'}
                        </button>
                      </div>
                      {linkError && <p className="link-error">{linkError}</p>}
                      {linkSuccess && <p className="link-success">{linkSuccess}</p>}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="alerts-section">
            <h2>Your Active Alerts</h2>

            {isLoading && (
              <div className="loading-message">Loading your alerts...</div>
            )}

            {error && (
              <div className="error-message">{error}</div>
            )}

            {!isLoading && !error && alerts.length === 0 && (
              <div className="no-alerts-message">
                <p>You don't have any active alerts yet.</p>
                <p>Click "Configure New Alert" to get started!</p>
              </div>
            )}

            {!isLoading && !error && alerts.length > 0 && (
              <div className="alerts-list">
                {courseNames.map(courseName => (
                  <div key={courseName} className="course-group">
                    <h3 className="course-group-name">{courseName}</h3>
                    <div className="course-alerts-list">
                      {groupedAlerts[courseName].map(alert => (
                        <div key={alert.id} className="alert-row">
                          <div className="alert-main-info">
                            <div className="alert-details">
                              {alert.dateSelectionMode === 'recurring' ? (
                                <>
                                  {formatRecurringDays(alert.recurringDays)} • {formatHour(alert.timeOfDayMinHour)}-{formatHour(alert.timeOfDayMaxHour)} • {alert.playerCount}p
                                </>
                              ) : (
                                <>
                                  {formatDate(alert.date)} • {formatHour(alert.timeOfDayMinHour)}-{formatHour(alert.timeOfDayMaxHour)} • {alert.playerCount}p
                                </>
                              )}
                            </div>
                          </div>
                          <div className="alert-actions">
                            <button
                              className={`toggle-button ${alert.active ? 'active' : 'inactive'}`}
                              onClick={() => handleToggleActive(alert.id)}
                              title={alert.active ? 'Deactivate alert' : 'Activate alert'}
                            >
                              {alert.active ? 'Active' : 'Inactive'}
                            </button>
                            <button
                              className="delete-button"
                              onClick={() => handleDeleteAlert(alert.id)}
                              title="Delete alert"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default DashboardPage;
